import ErrorService from "@/services/ErrorService";
import axios, { AxiosRequestConfig } from "axios";
import store from "@/store";
import { LOGOUT_USER } from "@/store/modules/auth/constants";
import i18n from "@/i18n";
import router from "@/router";
import { refresh_token } from "@/store/apis/auth";
import { clean_string } from "./global";
import { IApp } from "@/interfaces";
/**
 * axios interceptor for binding the access token with api call request
 * */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Portal Core HCS
const portalIsCoreHCS =
  clean_string(process.env.VUE_APP_PORTAL || "") ===
  clean_string(IApp.Portals.COREHCS);

// Portal Core 43 HCMS
const portalIsCoreHCMS =
  clean_string(process.env.VUE_APP_PORTAL || "") ===
  clean_string(IApp.Portals.COREHCMS);

export const requestInterceptor = function (): any {
  axios.interceptors.request.use(
    async function (config: AxiosRequestConfig) {
      // Append necessary information in headers
      if (config.headers) {
        const token = localStorage.getItem("token");
        if (token) config.headers.token = token;
        config.headers.domain =
          portalIsCoreHCS || portalIsCoreHCMS
            ? "https://recruitment.hcms.ai"
            : window.location.href;
        config.headers.lang = i18n.locale;
      }
      return config;
    },
    function (err) {
      return Promise.reject(err);
    }
  );
};

/**
 * axios interceptor for noticing error relevant to the api calling
 * */
// Prevents recursive 401 handling when the refresh-token endpoint itself returns 401
let isRefreshing = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const errorInterceptor = function (): any {
  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function (error): Promise<any> {
      // Response undefined means => backend didn't receive request from frontend
      // Means that firewall blocks backend app ip or any other issue
      if (!error.response) {
        error.response = {
          data: {
            message: i18n.t("errors.firewall-blocked")
          }
        };
      }
      // If response status is 401 => refresh token
      // Guard with isRefreshing to avoid an infinite loop when the refresh-token
      // endpoint itself returns 401 (which would otherwise re-trigger this handler).
      if (error.response.status === 401 && !isRefreshing) {
        const _refresh_token = localStorage.getItem("refresh_token");
        // If refresh token exist => get new token
        if (_refresh_token) {
          isRefreshing = true;
          try {
            // Get new token from refresh token
            const response = await refresh_token({
              refresh_token: _refresh_token
            });
            localStorage.setItem("token", response.data.data); // Update token in local storage
            isRefreshing = false;
            return axios.request(error.config); // Retry request
          } catch (e) {
            isRefreshing = false;
            // If failed to get new token => logout user
            await store.dispatch(`auth/${LOGOUT_USER}`);
            await router.push("/auth/login");
            throw "Invalid Refresh Token";
          }
        } else {
          // If refresh token not found => logout user
          await store.dispatch(`auth/${LOGOUT_USER}`);
          await router.push("/auth/login");
          throw "Invalid Refresh Token";
        }
      }
      ErrorService.onError(error.toString(), "VUE");
      return Promise.reject(error);
    }
  );
};
